import { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ClockClockwise, MagnifyingGlass, UserCircle, CalendarBlank, X, Users } from '@phosphor-icons/react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../App';

const toISODate = (d) => d.toISOString().split('T')[0];

const ROLE_STYLES = {
  admin: 'bg-purple-100 text-purple-800',
  owner: 'bg-amber-100 text-amber-800',
  manager: 'bg-blue-100 text-blue-800',
  receptionist: 'bg-green-100 text-green-800',
  staff: 'bg-gray-100 text-gray-800',
};

const formatTotalTime = (totalHours) => {
  if (totalHours === null || totalHours === undefined) return '-';
  const totalSeconds = Math.round(totalHours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-';
const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const StaffLoginLogout = () => {
  const { user } = useAuth();
  const { activeBranch } = useBranch();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(toISODate(new Date()));
  const [toDate, setToDate] = useState(toISODate(new Date()));
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('detailed');
  const [selectedStaff, setSelectedStaff] = useState(null);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (activeBranch?.branch_id) params.append('branch_id', activeBranch.branch_id);
      params.append('view', viewMode);
      const response = await api.get(`/login-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
    } catch (error) {
      toast.error('Failed to load login logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeBranch) fetchLogs();
  }, [fromDate, toDate, activeBranch, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRange = (daysBack, single = false) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - daysBack);
    setFromDate(toISODate(start));
    setToDate(toISODate(single ? start : end));
  };

  const handleReset = () => {
    setRange(0, true);
    setSearchQuery('');
    setSelectedStaff(null);
  };

  const staffList = useMemo(() => {
    const map = {};
    logs.forEach((log) => {
      if (!map[log.user_id]) {
        map[log.user_id] = { user_id: log.user_id, name: log.user_name, email: log.user_email, role: log.user_role, sessions: 0, hours: 0, active: false };
      }
      map[log.user_id].sessions += viewMode === 'summary' ? (log.session_count || 1) : 1;
      map[log.user_id].hours += log.total_hours || 0;
      if (log.status === 'active') map[log.user_id].active = true;
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [logs, viewMode]);

  const filteredLogs = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return logs.filter((log) => {
      if (selectedStaff && log.user_id !== selectedStaff.user_id) return false;
      if (!q) return true;
      return log.user_name?.toLowerCase().includes(q) || log.user_email?.toLowerCase().includes(q) || log.user_role?.toLowerCase().includes(q);
    });
  }, [logs, searchQuery, selectedStaff]);

  const groupedByDate = useMemo(() => {
    const groups = {};
    filteredLogs.forEach((log) => {
      const key = log.date || log.login_time?.slice(0, 10);
      (groups[key] = groups[key] || []).push(log);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredLogs]);

  const totalHours = filteredLogs.reduce((sum, l) => sum + (l.total_hours || 0), 0);
  const activeCount = filteredLogs.filter((l) => l.status === 'active').length;
  const canSeeAll = ['admin', 'owner', 'manager'].includes(user?.role);

  const isRangeActive = (daysBack, single) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - daysBack);
    return fromDate === toISODate(start) && toDate === toISODate(single ? start : end);
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6 max-w-full overflow-x-hidden" data-testid="staff-login-logout-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#2C4C3B] flex items-center gap-3">
              <ClockClockwise size={36} weight="fill" className="text-[#2C4C3B]" />
              Staff Login/Logout
            </h1>
            <p className="text-sm text-[#1C1C1A]/60 mt-1">
              {canSeeAll ? `Login and logout history for ${activeBranch?.name || 'selected branch'}` : 'Your login and logout history'}
            </p>
          </div>
          <div className="inline-flex rounded-md border border-[#E5E5DF] overflow-hidden text-sm self-start lg:self-auto" data-testid="login-log-view-toggle">
            <button type="button" data-testid="view-detailed-btn" onClick={() => setViewMode('detailed')}
              className={`px-3 py-1.5 font-medium transition-colors ${viewMode === 'detailed' ? 'bg-[#2C4C3B] text-white' : 'bg-white text-[#2C4C3B] hover:bg-[#F9F9F8]'}`}>
              Every Login/Logout
            </button>
            <button type="button" data-testid="view-summary-btn" onClick={() => setViewMode('summary')}
              className={`px-3 py-1.5 font-medium transition-colors ${viewMode === 'summary' ? 'bg-[#2C4C3B] text-white' : 'bg-white text-[#2C4C3B] hover:bg-[#F9F9F8]'}`}>
              Daily Summary
            </button>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 bg-white border-[#E5E5DF]">
          <div className="flex flex-col xl:flex-row xl:items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Today', d: 0, single: true, id: 'today' },
                { label: 'Yesterday', d: 1, single: true, id: 'yesterday' },
                { label: 'Last 7 Days', d: 7, single: false, id: '7d' },
                { label: 'Last 30 Days', d: 30, single: false, id: '30d' },
              ].map((r) => (
                <button key={r.id} type="button" data-testid={`range-${r.id}`} onClick={() => setRange(r.d, r.single)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${isRangeActive(r.d, r.single) ? 'bg-[#2C4C3B] text-white border-[#2C4C3B]' : 'bg-white text-[#2C4C3B] border-[#E5E5DF] hover:border-[#2C4C3B]'}`}>
                  {r.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <CalendarBlank size={18} className="text-[#2C4C3B] shrink-0" />
              <Input type="date" data-testid="from-date-input" value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); if (!toDate || e.target.value > toDate) setToDate(e.target.value); }}
                className="border-[#E5E5DF] h-9 min-w-0 flex-1" />
              <span className="text-xs text-[#1C1C1A]/50">to</span>
              <Input type="date" data-testid="to-date-input" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border-[#E5E5DF] h-9 min-w-0 flex-1" />
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1C1C1A]/40" />
              <Input type="text" data-testid="staff-search-input" placeholder="Search name, email or role…" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9 border-[#E5E5DF]" />
            </div>
            <Button onClick={handleReset} variant="outline" data-testid="reset-filters-btn" className="h-9 border-[#E5E5DF] text-[#2C4C3B]">
              Reset
            </Button>
          </div>
        </Card>

        {/* Staff selector */}
        {canSeeAll && staffList.length > 0 && (
          <Card className="p-4 bg-white border-[#E5E5DF]" data-testid="staff-filter-bar">
            <div className="flex items-center gap-2 mb-3">
              <Users size={18} className="text-[#2C4C3B]" />
              <p className="text-xs uppercase tracking-wider font-semibold text-[#2C4C3B]">Filter by staff</p>
              <span className="text-xs text-[#1C1C1A]/50">— click a name to see only their history</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="staff-chip-all" onClick={() => setSelectedStaff(null)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${!selectedStaff ? 'bg-[#2C4C3B] text-white border-[#2C4C3B]' : 'bg-white text-[#2C4C3B] border-[#E5E5DF] hover:border-[#2C4C3B]'}`}>
                All Staff ({staffList.length})
              </button>
              {staffList.map((s) => (
                <button key={s.user_id} type="button" data-testid={`staff-chip-${s.user_id}`}
                  onClick={() => setSelectedStaff(selectedStaff?.user_id === s.user_id ? null : s)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${selectedStaff?.user_id === s.user_id ? 'bg-[#2C4C3B] text-white border-[#2C4C3B]' : 'bg-white text-[#1C1C1A] border-[#E5E5DF] hover:border-[#2C4C3B]'}`}>
                  {s.active && <span className="w-2 h-2 rounded-full bg-green-500" title="Currently logged in" />}
                  <span className="font-medium">{s.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${selectedStaff?.user_id === s.user_id ? 'bg-white/20 text-white' : 'bg-[#F1F1EC] text-[#1C1C1A]/60'}`}>{s.sessions}</span>
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Selected staff banner + stats */}
        {selectedStaff && (
          <div data-testid="selected-staff-banner" className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-[#2C4C3B]/30 bg-[#EEF3EF] px-4 py-3">
            <div className="flex items-center gap-3">
              <UserCircle size={36} weight="fill" className="text-[#2C4C3B]" />
              <div>
                <p className="font-semibold text-[#2C4C3B]">{selectedStaff.name}</p>
                <p className="text-xs text-[#1C1C1A]/60">{selectedStaff.email} · <span className="capitalize">{selectedStaff.role}</span></p>
              </div>
            </div>
            <button type="button" data-testid="clear-staff-filter-btn" onClick={() => setSelectedStaff(null)}
              className="inline-flex items-center gap-1 text-sm text-[#2C4C3B] hover:text-[#C05C42]">
              <X size={14} /> Show all staff
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: viewMode === 'summary' ? 'Days' : 'Sessions', value: filteredLogs.length, cls: 'from-blue-50 to-blue-100 border-blue-200 text-blue-900', id: 'total' },
            { label: 'Currently Active', value: activeCount, cls: 'from-green-50 to-green-100 border-green-200 text-green-900', id: 'active' },
            { label: 'Completed', value: filteredLogs.length - activeCount, cls: 'from-purple-50 to-purple-100 border-purple-200 text-purple-900', id: 'completed' },
            { label: 'Total Time', value: formatTotalTime(totalHours), cls: 'from-amber-50 to-amber-100 border-amber-200 text-amber-900', id: 'hours' },
          ].map((c) => (
            <Card key={c.id} data-testid={`stat-${c.id}`} className={`p-4 bg-gradient-to-br ${c.cls}`}>
              <p className="text-xs font-medium opacity-80">{c.label}</p>
              <p className="text-2xl font-bold mt-1">{c.value}</p>
            </Card>
          ))}
        </div>

        {/* History */}
        <Card className="p-0 bg-white border-[#E5E5DF] overflow-hidden max-w-full">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#2C4C3B]"></div>
              <p className="mt-4 text-[#1C1C1A]/60">Loading logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12" data-testid="no-logs-message">
              <ClockClockwise size={56} weight="thin" className="mx-auto text-[#1C1C1A]/20 mb-3" />
              <p className="text-lg text-[#1C1C1A]/60">No login records for this selection</p>
              <p className="text-sm text-[#1C1C1A]/40 mt-1">Try a wider date range or clear the staff filter</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="login-history-table">
                <thead className="bg-[#F9F9F8]">
                  <tr className="border-b border-[#E5E5DF] text-left text-xs uppercase tracking-wider text-[#2C4C3B]">
                    <th className="py-3 px-4 font-semibold">Staff</th>
                    <th className="py-3 px-4 font-semibold">Role</th>
                    <th className="py-3 px-4 font-semibold">Login</th>
                    <th className="py-3 px-4 font-semibold">Logout</th>
                    <th className="py-3 px-4 font-semibold">Duration</th>
                    {viewMode === 'summary' && <th className="py-3 px-4 font-semibold">Sessions</th>}
                    <th className="py-3 px-4 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedByDate.map(([date, rows]) => (
                    <FragmentGroup key={date} date={date} rows={rows} viewMode={viewMode} canSeeAll={canSeeAll}
                      selectedStaff={selectedStaff} onSelectStaff={(row) => setSelectedStaff({ user_id: row.user_id, name: row.user_name, email: row.user_email, role: row.user_role })} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

const FragmentGroup = ({ date, rows, viewMode, canSeeAll, selectedStaff, onSelectStaff }) => {
  const dayHours = rows.reduce((s, r) => s + (r.total_hours || 0), 0);
  return (
    <>
      <tr className="bg-[#F4F6F4] border-y border-[#E5E5DF]" data-testid={`date-group-${date}`}>
        <td colSpan={viewMode === 'summary' ? 7 : 6} className="py-2 px-4 text-xs font-semibold text-[#2C4C3B]">
          <span className="inline-flex items-center gap-2">
            <CalendarBlank size={14} /> {formatDate(date)}
            <span className="font-normal text-[#1C1C1A]/50">· {rows.length} {rows.length === 1 ? 'record' : 'records'} · {formatTotalTime(dayHours)}</span>
          </span>
        </td>
      </tr>
      {rows.map((log) => (
        <tr key={log.log_id} className="border-b border-[#E5E5DF] hover:bg-[#F9F9F8] transition-colors" data-testid={`log-row-${log.log_id}`}>
          <td className="py-3 px-4">
            <button type="button" disabled={!canSeeAll} onClick={() => onSelectStaff(log)} data-testid={`staff-name-${log.log_id}`}
              className={`text-left ${canSeeAll ? 'hover:underline' : ''} ${selectedStaff?.user_id === log.user_id ? 'text-[#2C4C3B]' : 'text-[#1C1C1A]'}`}>
              <span className="block text-sm font-medium">{log.user_name}</span>
              <span className="block text-xs text-[#1C1C1A]/50">{log.user_email}</span>
            </button>
          </td>
          <td className="py-3 px-4">
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium capitalize ${ROLE_STYLES[log.user_role] || ROLE_STYLES.staff}`}>{log.user_role}</span>
          </td>
          <td className="py-3 px-4 text-sm text-[#1C1C1A] whitespace-nowrap">{formatTime(log.login_time)}</td>
          <td className="py-3 px-4 text-sm whitespace-nowrap">
            {log.logout_time ? <span className="text-[#1C1C1A]">{formatTime(log.logout_time)}</span> : <span className="text-green-600 font-medium">Still active</span>}
          </td>
          <td className="py-3 px-4 text-sm text-[#1C1C1A] font-medium whitespace-nowrap">{formatTotalTime(log.total_hours)}</td>
          {viewMode === 'summary' && <td className="py-3 px-4 text-sm text-[#1C1C1A]">{log.session_count}</td>}
          <td className="py-3 px-4">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${log.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
              {log.status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />}
              {log.status === 'active' ? 'Active' : log.auto_closed ? 'Closed (auto)' : 'Completed'}
            </span>
          </td>
        </tr>
      ))}
    </>
  );
};

export default StaffLoginLogout;
